import React, { useCallback, useState, useMemo } from 'react';

import { andThen, identity, or, otherwise, pipe, tap } from 'ramda';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';

import { SNACKBAR_SKIN } from 'components/shared/Snackbar';
import { PAGE_STATUS_APPROVED, PAGE_STATUS_DRAFT, PENDING_CONTENT } from 'constants/index';
import { GENERAL_ERROR_MESSAGE } from 'constants/texts';
import { showSnackbar } from 'hooks/useSnackbar';
import { getPagePoliciesSelector, getPageStatusSelector, pageDataSelector } from 'store/page/selectors';
import { appHasChangesSelector } from 'store/unsavedChanges/selectors';
import { isAgentSelector } from 'store/user/selectors';
import { confirmAlert } from 'utils/alert';

import { usePushUpdatesNotify } from '../../utils';
import { usePageContext } from '../context';

export function usePageButtons({
  values,
  savePageProps,
  publishPageProps,
  onSave,
  prepareValues = identity,
  submitting,
}) {
  const [isSaving, setIsSaving] = useState(false);
  const doesPageHaveChanges = useSelector(appHasChangesSelector);
  const { isCanPending } = useSelector(getPagePoliciesSelector);
  const pageStatus = useSelector(getPageStatusSelector);
  const { libType } = useParams();
  const { usePublishPageHandler, useSavePageHandler } = usePageContext();
  const errorSnackbar = () => showSnackbar({ text: GENERAL_ERROR_MESSAGE, type: SNACKBAR_SKIN.ERROR });

  const save = useSavePageHandler(savePageProps);
  const handleSave = useCallback(
    () =>
      pipe(
        tap(() => setIsSaving(true)),
        prepareValues,
        onSave || save,
        otherwise(errorSnackbar),
        andThen(() => setIsSaving(false)),
      )(values),
    [onSave, prepareValues, save, values],
  );

  const handlePublishWithConfirmation = usePublishPageHandler({ ...publishPageProps, withConfirmation: true });
  const handlePublishWithoutConfirmation = usePublishPageHandler(publishPageProps);
  const handlePublish = useCallback(
    (values, { withConfirmation = false } = {}) => {
      const { handler } = withConfirmation ? handlePublishWithConfirmation : handlePublishWithoutConfirmation;
      return handler(prepareValues(values));
    },
    [handlePublishWithConfirmation, handlePublishWithoutConfirmation, prepareValues],
  );

  // const handleDelete = useDeletePageHandler();
  const page = useSelector(pageDataSelector);
  const isAgent = useSelector(isAgentSelector);
  const isPendingContent = libType === PENDING_CONTENT;
  const isShownPublishBtn = !isPendingContent && pageStatus !== PAGE_STATUS_APPROVED && !isAgent;

  const pushUpdatesNotify = usePushUpdatesNotify(values);

  const shouldShowPageApprovedAlert = !isAgent && page.status === PAGE_STATUS_APPROVED;

  const handleSaveAndPublish = useCallback(async () => {
    await handleSave(values);

    // for custom onSave should be implemented with yourself
    if (doesPageHaveChanges && !onSave) {
      await pushUpdatesNotify();
    }
  }, [values, handleSave, pushUpdatesNotify, doesPageHaveChanges, onSave]);

  const showPageApprovedAlert = useCallback(() => {
    confirmAlert({
      title: 'Great Job!',
      hintBottom: (
        <>
          Click “Save and Publish” below to update this page
          <br />
          in the Safari Portal Content Library.
        </>
      ),
      confirmButtonText: 'Save and Publish',
      cancelButtonText: 'Cancel',
      onConfirm: handleSaveAndPublish,
    });
  }, [handleSaveAndPublish]);

  // agent's saving logic section - page is already approved and new changes have been made
  const shouldShowAgentPushUpdateNotify = isAgent && page.status === PAGE_STATUS_APPROVED;

  // logic for non agent's account - the page is in a draft status
  const shouldShowPageIsReadyToPublishAlert = !isAgent && page.status === PAGE_STATUS_DRAFT;
  const showPageIsReadyToPublishAlert = useCallback(() => {
    confirmAlert({
      title: (
        <>
          Your content has been saved as a Draft <br /> in your content library.
        </>
      ),
      hintBottom:
        'If you are ready to make this page live and share with agents and potential guests, please press Publish.',
      confirmButtonText: 'Publish',
      cancelButtonText: 'Save as Draft',
      onConfirm: () => {
        handlePublish(values);
      },
      onCancel: () => {
        handleSave(values);
      },
    });
  }, [values, handleSave, handlePublish]);

  const saveHandler = useMemo(() => {
    if (shouldShowPageApprovedAlert) return showPageApprovedAlert;
    else if (shouldShowAgentPushUpdateNotify) return handleSaveAndPublish;
    else if (shouldShowPageIsReadyToPublishAlert) return showPageIsReadyToPublishAlert;

    return handleSave;
  }, [
    handleSave,
    shouldShowPageApprovedAlert,
    showPageApprovedAlert,
    shouldShowAgentPushUpdateNotify,
    shouldShowPageIsReadyToPublishAlert,
    showPageIsReadyToPublishAlert,
    handleSaveAndPublish,
  ]);

  const isDisabledButton = pipe(
    or(submitting),
    or(isSaving),
    or(handlePublishWithConfirmation.isLoading),
    or(handlePublishWithoutConfirmation.isLoading),
  )(false);

  return {
    doesPageHaveChanges,
    // isCanDestroy,
    isCanPending,
    // handleDelete,
    handlePublish,
    saveHandler,
    isShownPublishBtn,
    isDisabledButton,
  };
}
